import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { moveStep, type MoveStepRequest } from '@/api/steps'
import { PROJECTS_QUERY_KEY } from './use-projects'
import type { ApiError, StepNode } from '@/api/types'

export interface StepCoords {
  step: StepNode
  parent: StepNode | null
  siblings: StepNode[]
  /** Position of {@code step} within {@code siblings}. */
  index: number
  /**
   * The grandparent's id (i.e. the new parentStepId we'd use when outdenting). Null when the
   * grandparent is the root level — outdent in that case clears parent.
   */
  grandparentId: string | null
  /**
   * Siblings of {@code parent} (i.e. the level we'd outdent into). Null when {@code step} is at
   * the root.
   */
  parentSiblings: StepNode[] | null
  /** {@code parent}'s index in {@code parentSiblings}. Null when {@code step} is at the root. */
  parentIndex: number | null
}

const apiErrorMessage = (err: unknown, fallback: string): string =>
  (err as ApiError | undefined)?.detail ?? fallback

export interface MoveActions {
  canMoveUp: boolean
  canMoveDown: boolean
  canIndent: boolean
  canOutdent: boolean
  moveUp: () => void
  moveDown: () => void
  indent: () => void
  outdent: () => void
}

/**
 * Phase 11a: single-PATCH atomic move via `PATCH /api/v1/projects/{id}/tree/move`. Replaces the
 * older two-PATCH swap chain that left a duplicate `sortOrder` between writes. Used by both the
 * chevron move-up/-down buttons (kept as keyboard fallback) and by drag-and-drop.
 */
export const useMoveStep = (projectId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: MoveStepRequest) => moveStep(projectId, data),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [...PROJECTS_QUERY_KEY, 'detail', projectId],
      })
    },
  })
}

/**
 * Returns the four move actions plus capability flags for a single step. The capability flags
 * mean: <em>does this action have an effect?</em> — disabled buttons should reflect them.
 *
 * All four actions route through the same atomic `tree/move` endpoint:
 *   - move up / down → within-parent reorder (newParentId omitted)
 *   - indent → cross-parent move under the previous sibling
 *   - outdent → cross-parent move into the grandparent's sibling list
 */
export const useStepMoves = (projectId: string, coords: StepCoords): MoveActions => {
  const move = useMoveStep(projectId)
  const { step, parent, siblings, index, grandparentId, parentSiblings, parentIndex } = coords

  const canMoveUp = index > 0
  const canMoveDown = index < siblings.length - 1
  const canIndent = index > 0 // there is a previous sibling to nest under
  const canOutdent = parent !== null // outdent only makes sense if we have a parent

  const onMoveError = (label: string) => (err: unknown) =>
    toast.error(apiErrorMessage(err, `Failed to ${label} step`))

  const moveUp = () => {
    if (!canMoveUp) return
    move.mutate({ stepId: step.id, newSortOrder: index - 1 }, { onError: onMoveError('move') })
  }

  const moveDown = () => {
    if (!canMoveDown) return
    move.mutate({ stepId: step.id, newSortOrder: index + 1 }, { onError: onMoveError('move') })
  }

  const indent = () => {
    if (!canIndent) return
    const newParent = siblings[index - 1]
    move.mutate(
      {
        stepId: step.id,
        newParentId: newParent.id,
        newSortOrder: newParent.children.length,
      },
      { onError: onMoveError('indent') }
    )
  }

  const outdent = () => {
    if (!canOutdent || parentSiblings === null || parentIndex === null) return
    // Outdent: move into the grandparent's sibling list, just after the current parent.
    move.mutate(
      {
        stepId: step.id,
        newParentId: grandparentId,
        newSortOrder: parentIndex + 1,
      },
      { onError: onMoveError('outdent') }
    )
  }

  return { canMoveUp, canMoveDown, canIndent, canOutdent, moveUp, moveDown, indent, outdent }
}
